import { useState, useRef, useEffect } from "react";
import { Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const PHONE_NUMBERS = [
  { label: "Primary", number: "+919933237121", display: "+91 99332 37121" },
  { label: "Secondary", number: "+919434297214", display: "+91 94342 97214" },
];

// Floating Call button (used globally)
export const FloatingCall = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="fixed bottom-6 right-24 z-50">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full right-0 mb-3 bg-white rounded-xl shadow-xl border border-border overflow-hidden min-w-[240px]"
            data-testid="call-menu"
          >
            <div className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold">
              Choose a number to call
            </div>
            {PHONE_NUMBERS.map((phone, i) => (
              <a
                key={phone.number}
                href={`tel:${phone.number}`}
                data-testid={`call-number-${i}`}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors border-b last:border-b-0"
              >
                <div className="w-9 h-9 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-secondary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{phone.label}</p>
                  <p className="text-sm font-medium text-primary">{phone.display}</p>
                </div>
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen(!open)}
        data-testid="floating-call-btn"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-4 shadow-lg relative"
        style={{ boxShadow: "0 4px 20px rgba(10, 25, 47, 0.4)" }}
        aria-label="Call us"
      >
        <Phone size={28} />
      </motion.button>
    </div>
  );
};

// Inline Call button dropdown (used on package detail page)
export const InlineCallButton = ({ packageName }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative w-full mt-3">
      <button
        onClick={() => setOpen(!open)}
        data-testid="inline-call-btn"
        className="w-full inline-flex items-center justify-center h-12 rounded-md bg-secondary hover:bg-secondary/90 text-secondary-foreground font-medium transition-colors"
      >
        <Phone size={20} className="mr-2" />
        Enquire via Call
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-border overflow-hidden z-20"
            data-testid="inline-call-menu"
          >
            {PHONE_NUMBERS.map((phone, i) => (
              <a
                key={phone.number}
                href={`tel:${phone.number}`}
                data-testid={`inline-call-number-${i}`}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors border-b last:border-b-0"
              >
                <div className="w-9 h-9 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-secondary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{phone.label}</p>
                  <p className="text-sm font-medium text-primary">{phone.display}</p>
                </div>
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FloatingCall;
